const MAX_DIMENSION = 1920;
const FULL_HD_QUALITY = 0.9;
export const MAX_STUDENT_PHOTO_UPLOAD_BYTES = 1024 * 1024;

const loadImageElement = (src: string) => new Promise<HTMLImageElement>((resolve, reject) => {
  const image = new Image();

  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('Não foi possível processar a imagem selecionada.'));
  image.src = src;
});

const canvasToBlob = (canvas: HTMLCanvasElement, quality: number) => new Promise<Blob>((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (!blob) {
      reject(new Error('Não foi possível preparar a imagem para envio.'));
      return;
    }

    resolve(blob);
  }, 'image/jpeg', quality);
});

const drawImageToCanvas = async (file: File, quality: number): Promise<File> => {
  const sourceUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageElement(sourceUrl);
    const { naturalWidth: width, naturalHeight: height } = image;

    const isLandscape = width >= height;
    const targetWidth = isLandscape ? MAX_DIMENSION : Math.round((width / height) * MAX_DIMENSION);
    const targetHeight = isLandscape ? Math.round((height / width) * MAX_DIMENSION) : MAX_DIMENSION;

    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Não foi possível processar a imagem selecionada.');
    }

    context.drawImage(image, 0, 0, targetWidth, targetHeight);
    const blob = await canvasToBlob(canvas, quality);
    const baseName = file.name.replace(/\.[^.]+$/, '').trim() || 'photo';

    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
};

const compressToMaxSize = async (file: File, maxBytes: number): Promise<File> => {
  let quality = FULL_HD_QUALITY;
  let processed = file;

  while (quality >= 0.4) {
    processed = await drawImageToCanvas(file, quality);

    if (processed.size <= maxBytes) {
      return processed;
    }

    quality -= 0.1;
  }

  return processed;
};

export const resizeToFullHD = async (file: File): Promise<File> => {
  const sourceUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageElement(sourceUrl);
    const { naturalWidth: width, naturalHeight: height } = image;

    if (width <= MAX_DIMENSION && height <= MAX_DIMENSION && file.type === 'image/jpeg') {
      return file;
    }

    return drawImageToCanvas(file, FULL_HD_QUALITY);
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
};

export const prepareStudentPhotoFile = async (file: File): Promise<File> => {
  const resized = await resizeToFullHD(file);
  const compressed = await compressToMaxSize(resized, MAX_STUDENT_PHOTO_UPLOAD_BYTES);

  if (compressed.size > MAX_STUDENT_PHOTO_UPLOAD_BYTES) {
    throw new Error('Não foi possível reduzir a imagem para no máximo 1 MB.');
  }

  return compressed;
};

const fileToBase64 = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onloadend = () => {
    if (typeof reader.result !== 'string') {
      reject(new Error('Não foi possível preparar a imagem para envio.'));
      return;
    }

    resolve(reader.result);
  };

  reader.onerror = () => reject(new Error('Não foi possível preparar a imagem para envio.'));
  reader.readAsDataURL(file);
});

export const fileToBase64DataUrl = fileToBase64;
